import React from 'react';
import {
  Box,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  VStack
} from "@chakra-ui/react";

interface FuelGaugeProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  colorScheme?: 'fuel' | 'temperature' | 'pressure';
}

const FuelGauge: React.FC<FuelGaugeProps> = ({
  label,
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  unit = '%',
  colorScheme = 'fuel'
}) => {
  const getColor = () => {
    const percentage = ((value - min) / (max - min)) * 100;
    
    switch (colorScheme) {
      case 'fuel':
        return percentage < 25 ? "#E53E3E" : percentage < 50 ? "#D69E2E" : "#38A169";
      case 'temperature':
        return percentage > 75 ? "#E53E3E" : percentage > 50 ? "#D69E2E" : "#38A169";
      case 'pressure':
        return percentage < 20 || percentage > 80 ? "#E53E3E" : percentage < 40 || percentage > 60 ? "#D69E2E" : "#38A169";
      default:
        return "#3182CE";
    }
  };

  const getChakraColor = () => {
    const percentage = ((value - min) / (max - min)) * 100;
    
    switch (colorScheme) {
      case 'fuel':
        return percentage < 25 ? "red.400" : percentage < 50 ? "yellow.400" : "green.400";
      case 'temperature':
        return percentage > 75 ? "red.400" : percentage > 50 ? "yellow.400" : "green.400";
      case 'pressure':
        return percentage < 20 || percentage > 80 ? "red.400" : percentage < 40 || percentage > 60 ? "yellow.400" : "green.400";
      default:
        return "blue.400";
    }
  };

  const getDisplayValue = () => {
    if (unit === '%') {
      const percentage = ((value - min) / (max - min)) * 100;
      return `${Math.round(percentage)}${unit}`;
    }
    return `${value}${unit}`;
  };

  return (
    <VStack spacing={4} w="100%">
      <Text fontWeight="bold" fontSize="md" color="gray.700" textAlign="center">
        {label}
      </Text>

      {/* Custom Liquid Gauge */}
      <Box position="relative" display="flex" alignItems="center" justifyContent="center">
        <Box
          width="120px"
          height="120px"
          borderRadius="50%"
          border="3px solid"
          borderColor="gray.300"
          position="relative"
          overflow="hidden"
          bg="gray.50"
        >
          {/* Liquid Fill */}
          <Box
            position="absolute"
            bottom="0"
            left="0"
            right="0"
            bg={getColor()}
            height={`${((value - min) / (max - min)) * 100}%`}
            transition="all 0.8s ease-in-out"
            _before={{
              content: '""',
              position: "absolute",
              top: "-5px",
              left: "0",
              width: "100%",
              height: "10px",
              bg: getColor(),
              borderRadius: "50%",
              animation: "liquidWave 3s ease-in-out infinite alternate",
            }}
            _after={{
              content: '""',
              position: "absolute",
              top: "-3px",
              left: "0",
              width: "100%",
              height: "6px",
              bg: getColor(),
              borderRadius: "50%",
              animation: "liquidWave 2s ease-in-out infinite alternate-reverse",
              opacity: 0.7,
            }}
            sx={{
              "@keyframes liquidWave": {
                "0%": { transform: "scaleX(1) scaleY(1)" },
                "50%": { transform: "scaleX(1.1) scaleY(0.8)" },
                "100%": { transform: "scaleX(0.9) scaleY(1.2)" },
              },
            }}
          />
          
          {/* Value Text */}
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            zIndex={2}
            textAlign="center"
          >
            <Text 
              fontSize="lg" 
              fontWeight="bold" 
              color="gray.800"
              textShadow="0 0 3px rgba(255,255,255,0.8)"
            >
              {getDisplayValue()}
            </Text>
          </Box>
        </Box>
      </Box>

      {/* Slider to adjust value */}
      <Box w="100%" px={2}>
        <Slider 
          min={min} 
          max={max} 
          step={step} 
          value={value} 
          onChange={onChange}
          focusThumbOnChange={false}
        >
          <SliderTrack bg="gray.100">
            <SliderFilledTrack bg={getChakraColor()} />
          </SliderTrack>
          <SliderThumb boxSize={4} bg={getChakraColor()} />
        </Slider>
        <Box display="flex" justifyContent="space-between" mt={1}>
          <Text fontSize="xs" color="gray.500">{min}{unit}</Text>
          <Text fontSize="xs" color="gray.500">{max}{unit}</Text>
        </Box>
      </Box>
    </VStack>
  );
};

export default FuelGauge;